import Typography from '@mui/material/Typography';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ErrorCode } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { StatusCode } from '@swf/core/enums/statusCode';
import useErrorHandling from '@swf/core/hooks/useErrorHandling';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import { ServerMessageHandler } from '@swf/notification';

import type { DepositResult } from '../../types/Cashier';
import {
  AamsErrorCode,
  AccountErrorCode,
  CashoutStatus,
  DERegulatoryErrorCode,
  DepositStatus,
  ESRegulatoryErrorCode,
  LugasErrorCode,
  PaySystemErrorCode,
  RealMoneyStatus,
  VerificationErrorCode
} from '../../types/status';
import Prompt from '../Prompt/Prompt';
import useIsCARestrictedState from '../Signup/hooks/useIsCARestrictedState';

import AAMSErrors from './AAMSErrors/AAMSErrors';
import AuthenticationRequired from './AuthenticationRequired/AuthenticationRequired';
import BonusAcknowledgement from './BonusAcknowledgement/BonusAcknowledgement';
import DKBlacklistError from './DKBlacklistError/DKBlacklistError';
import DKMitIdAuthError from './DKMitIdAuthError/DKMitIdAuthError';
import ESRegulatoryErrorPrompts from './ESRegulatoryErrors/ESRegulatoryErrors';
import ElectronicVerificationErrors from './ElectronicVerificationErrors/ElectronicVerificationErrors';
import GlobalError from './GlobalError/GlobalError';
import LessMoney from './LessMoney/LessMoney';
import LugasErrors from './LugasErrors/LugasErrors';
import MethodVerificationRequired from './MethodVerificationRequired/MethodVerificationRequired';
import MitIdRedirectRequired from './MitIdRedirectRequired/MitIdRedirectRequired';
import MobilePhoneValidationErrors from './MobilePhoneValidationErrors/MobilePhoneValidationErrors';
import MobileValidationErrors from './MobileValidationErrors/MobileValidationErrors';
import NoBankTransfersAvailable from './NoBankTransfersAvailable/NoBankTransfersAvailable';
import NotAvailable from './NotAvailable/NotAvailable';
import OasisErrors from './OasisErrors/OasisErrors';
import PepHioAndThirdPartyCheck from './PepHioAndThirdPartyCheck/PepHioAndThirdPartyCheck';
import PlayerBlacklisted from './PlayerBlacklisted/PlayerBlacklisted';
import {
  TXTSRV_Please_try_again_laterX,
  TXTWCL_Okay,
  TXTWCL_SorryX_somethingXs_gone_wrong__ELL,
  TXTWCL_There_has_been_an_issue_with_y_ELL
} from './Prompts.messages.json';
import RMFExistingAccount from './RMFExistingAccount/RMFExistingAccount';
import RMFRequired from './RMFRequired/RMFRequired';
import RejectedBonusMinDeposit from './RejectedBonus/RejectedBonusMinDeposit';
import RepeatedDecline from './RepeatedDecline/RepeatedDecline';
import RestrictedState from './RestrictedState/RestrictedState';
import SuggestedPayment from './SuggestedPayment/SuggestedPayment';
import TempAccountLimit from './TempAccountLimit/TempAccountLimit';
import UserHasPendingCashoutError from './UserHasPendingCashoutError/UserHasPendingCashoutError';
import WbtBankNotAvailable from './WbtBankNotAvailable/WbtBankNotAvailable';
import WithdrawMethodNotAvailable from './WithdrawMethodNotAvailable/WithdrawMethodNotAvailable';

const Prompts: React.FC = () => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const siteId = useSelector(select.cms.getSiteId);
  const isDe = siteId === Sites.DE;
  const isDK = siteId === Sites.DEN;
  const isES = siteId === Sites.SPA;
  const isPt = siteId === Sites.POR;

  const ES_REGULATORY_ERRORS = [
    ESRegulatoryErrorCode.CLOSE_RM_FORM,
    ESRegulatoryErrorCode.ID_DUPLICATE_FOUND,
    ESRegulatoryErrorCode.INCORRECT_DOB,
    ESRegulatoryErrorCode.INCORRECT_FIRST_NAME,
    ESRegulatoryErrorCode.INCORRECT_LAST_NAME,
    ESRegulatoryErrorCode.INCORRECT_SECOND_LAST_NAME,
    ESRegulatoryErrorCode.SERVICE_NOT_AVAILABLE
  ];

  const DK_VALIDATION_ERRORS = [
    RealMoneyStatus.NOT_ALLOWED,
    RealMoneyStatus.RM_DATA_REJECTED_BY_REGULATOR,
    RealMoneyStatus.DK_CPR_VALIDATION_ERROR
  ];

  const form = useForm();
  const fromEvent = useFromEvent();
  const {
    errors: [error],
    removeError
  } = useErrorHandling();

  const {
    data: { depositResult, paySystems },
    values: { deposit }
  } = useWizardContext();
  const onClose = () => removeError(error);
  const isCARestrictedStateFieldError = useIsCARestrictedState();

  const { globalErrors, fieldErrors } = error || {};

  useEffect(() => {
    const handleMessage = ({ detail }: CustomEvent) => {
      const { message } = detail.payload;

      if (message === 'rmfCompleted') {
        form.submit();
      }
    };

    if (
      isEmbedded &&
      globalErrors?.length &&
      globalErrors[0].code === PaySystemErrorCode.RMFRequired
    ) {
      fromEvent(document, 'starsbroadcast').subscribe((e) => handleMessage(e as CustomEvent));
    }
  }, [form, fromEvent, globalErrors, isEmbedded]);

  if (!error || (fieldErrors?.length && !isCARestrictedStateFieldError)) {
    return null;
  }

  if (isCARestrictedStateFieldError) {
    return <RestrictedState onClose={onClose} />;
  }

  if (globalErrors?.length) {
    const [{ code, defaultMessage }] = globalErrors;
    switch (true) {
      case code === AccountErrorCode.MIT_ID_AUTH_REQUIRED:
        return <MitIdRedirectRequired />;
      case code === PaySystemErrorCode.MethodVerificationRequired:
        return <MethodVerificationRequired onClose={onClose} />;
      case DK_VALIDATION_ERRORS.includes(code as RealMoneyStatus):
        return null;
      case isDK && code === RealMoneyStatus.PLAYER_BLACKLISTED:
        return <DKBlacklistError />;
      case isDK && code === AccountErrorCode.MIT_ID_AUTH_FAILED:
        return <DKMitIdAuthError />;
      case code === StatusCode.PLAYER_BLACKLISTED.toString():
      case code === StatusCode.ERR_ACCOUNT_CREATION_FAILED.toString():
        return null;
      case code === VerificationErrorCode.FiscalCodeAlreadyExists:
        return <RMFExistingAccount onClose={onClose} />;
      case code === VerificationErrorCode.ElectronicVerificationFailed:
      case code === VerificationErrorCode.FiscalCodeLen9Required:
      case code === VerificationErrorCode.ElectronicVerificationFailedTooManyRequests:
      case isDe && code === DERegulatoryErrorCode.SERVICE_NOT_AVAILABLE:
        return <ElectronicVerificationErrors onClose={onClose} code={code as string} />;
      case code === PaySystemErrorCode.NotAvailable && !!defaultMessage:
        return <NotAvailable defaultMessage={defaultMessage as string} onClose={onClose} />;
      case code === PaySystemErrorCode.AuthenticationRequired:
        return <AuthenticationRequired onClose={onClose} />;
      case code === PaySystemErrorCode.WithdrawMethodNotAvailable && !!defaultMessage:
        return (
          <WithdrawMethodNotAvailable defaultMessage={defaultMessage as string} onClose={onClose} />
        );
      case code === PaySystemErrorCode.WbtBankNotAvailable:
        return <WbtBankNotAvailable onClose={onClose} />;
      case code === PaySystemErrorCode.HostileCard:
        return (
          <SuggestedPayment cardType={deposit.type} paySystems={paySystems} onClose={onClose} />
        );
      case code === PaySystemErrorCode.NoBankTransfers:
        return <NoBankTransfersAvailable onClose={onClose} />;
      case deposit && depositResult && code === PaySystemErrorCode.RateChanged:
        return (
          <LessMoney
            depositResult={depositResult as DepositResult}
            deposit={deposit}
            onClose={onClose}
          />
        );
      case code === PaySystemErrorCode.RepeatedCashinDeclineOptions:
        return <RepeatedDecline onClose={onClose} />;
      case code === PaySystemErrorCode.RMFRequired || code === CashoutStatus.RMF_REQUIRED:
        return <RMFRequired onClose={onClose} />;
      case code === PaySystemErrorCode.BonusAcknowledgement:
        return <BonusAcknowledgement onClose={onClose} />;
      case code === DepositStatus.REJECTED_BONUS_MIN_DEPOSIT_REQUIRED && !!defaultMessage:
        return (
          <RejectedBonusMinDeposit
            deposit={deposit}
            defaultMessage={defaultMessage as string}
            onClose={onClose}
          />
        );
      case code === PaySystemErrorCode.TempAccountLimitReached:
        return <TempAccountLimit onClose={onClose} />;
      case code === PaySystemErrorCode.PepHioAndThirdPartyCheck:
        return <PepHioAndThirdPartyCheck onClose={onClose} />;
      case code === CashoutStatus.NOT_ALLOWED:
      case code === CashoutStatus.PENDING_CASHOUTS:
      case code === PaySystemErrorCode.PaySystemsNotAvailable:
        return null;
      case deposit?.type && code === RealMoneyStatus.PLAYER_BLACKLISTED:
        return null;
      case deposit === undefined && code === RealMoneyStatus.PLAYER_BLACKLISTED:
        return <PlayerBlacklisted onClose={onClose} />;
      case deposit?.type && code === RealMoneyStatus.DEPOSIT_REQUEST_CANNOT_BE_PROCESSED:
        return <PlayerBlacklisted onClose={onClose} />;
      case code === AamsErrorCode.TEMPORARY_TECHNICAL_ISSUE:
      case code === AamsErrorCode.UNSUCCESSFUL_MATCH:
      case code === AamsErrorCode.REQUEST_STILL_BEING_PROCESSED:
      case code === AamsErrorCode.ACCOUNT_ALREADY_EXISTS:
      case code === AamsErrorCode.ACCOUNT_ALREADY_EXISTS_ONE:
      case code === AamsErrorCode.PROVINCE_OF_RESIDENCE_NOT_VALID:
      case code === AamsErrorCode.PLAYER_UNDER_AGE:
      case code === AamsErrorCode.INFORMATION_IS_INVALID:
      case code === AamsErrorCode.FISCAL_CODE_IS_NOT_VALID:
      case code === AamsErrorCode.UNKNOWN_AAMS_ERROR:
        return <AAMSErrors onClose={onClose} code={code as string} />;
      case code === ErrorCode.USER_HAS_PENDING_CASHOUTS:
        return <UserHasPendingCashoutError onClose={onClose} />;
      case code === RealMoneyStatus.INVALID_MOBILE_CODE:
      case code === RealMoneyStatus.EXCEEDED_VALIDATION_ATTEMPTS:
        return <MobileValidationErrors onClose={onClose} code={code as string} />;
      case code === RealMoneyStatus.USER_BANNED_BY_REGULATOR && isDe:
      case code === RealMoneyStatus.DBM_ACCOUNT_CLOSED && (isDe || isPt):
      case code === RealMoneyStatus.BLACKLIST_SERVICE_NOT_AVAILABLE && !!defaultMessage:
      case code === RealMoneyStatus.DE_BANNED_BY_REGULATOR:
      case code === RealMoneyStatus.DE_UNBANNED_BY_REGULATOR:
        return (
          <OasisErrors
            onClose={onClose}
            code={code as RealMoneyStatus}
            defaultMessage={defaultMessage as string}
          />
        );
      case code === LugasErrorCode.ERR_LUGAS_UNAVAILABLE:
      case code === LugasErrorCode.ERR_LUGAS_LIMIT_REQUIRED:
      case code === LugasErrorCode.ERR_LUGAS_DUPLICATE_PLAYER_ID:
      case code === LugasErrorCode.ERR_LUGAS_REQUEST_FAILED:
        return <LugasErrors onClose={onClose} code={code as LugasErrorCode} />;
      case isES &&
        code !== ESRegulatoryErrorCode.CLOSE_RM_FORM &&
        ES_REGULATORY_ERRORS.includes(code as ESRegulatoryErrorCode):
        return <ESRegulatoryErrorPrompts code={code as string} onClose={onClose} />;
      case code === ESRegulatoryErrorCode.CLOSE_RM_FORM:
        return <PlayerBlacklisted code={code as string} onClose={onClose} />;
      case code === RealMoneyStatus.INVALID_MOBILE_PHONE:
        return <MobilePhoneValidationErrors onClose={onClose} />;
      case deposit && code && !!defaultMessage:
        return <GlobalError defaultMessage={defaultMessage as string} onClose={onClose} />;
    }
  }

  return (
    <Prompt
      variant="error"
      title={{ translation: TXTWCL_There_has_been_an_issue_with_y_ELL }}
      primaryButton={{ translation: TXTWCL_Okay }}
      onClose={onClose}
    >
      <Typography>
        {globalErrors?.length && globalErrors[0].defaultMessage ? (
          <ServerMessageHandler message={globalErrors[0].defaultMessage} />
        ) : (
          <>
            <Translation {...TXTWCL_SorryX_somethingXs_gone_wrong__ELL} />{' '}
            <Translation {...TXTSRV_Please_try_again_laterX} />
          </>
        )}
      </Typography>
    </Prompt>
  );
};

export default Prompts;
